import type { PaginationDto, PaginationResponseDto } from "core/dto";
import { LOCALE, ORDER_BY } from "core/interfaces";
import type { PageMetaDto } from "shared/dto";

import { getCollectionDefault } from "~/common/defaults/collection.default";
import { getPaginationDefault } from "~/common/defaults/pagination.default";
import { getPaginationResponseDefault } from "~/common/defaults/pagination-response.default";
import { collectionNormalize } from "~/common/helpers/collection-adapter";
import type { CreateCollectionDto } from "~/common/interfaces/dto/collections/create-collection.dto";
import type { GetAllCollectionsDto } from "~/common/interfaces/dto/collections/get-all-collections.dto";
import type { UpdateCollectionByIdDto } from "~/common/interfaces/dto/collections/update-collection-by-id.dto";
import type { IdDto } from "~/common/interfaces/dto/id.dto";
import type { Collection, CollectionAdapted } from "~/common/interfaces/entities/collection.interface";
import type { Sort } from "~/common/interfaces/sort.interface";

export const useCollectionsStore = defineStore(
	"collections",
	() => {
		const { locale } = useI18n();
		const collectionNormalizeWithLocale = collectionNormalize.bind(this, locale as Ref<LOCALE>);

		const currentCollection = ref<CollectionAdapted>(getCollectionDefault());
		const collections = ref<CollectionAdapted[]>([]);
		const meta = ref<PageMetaDto>(getPaginationResponseDefault());
		const recentGetAllQuery = ref<GetAllCollectionsDto & PaginationDto>();
		const tableSort = ref<Sort>({
			columns: [],
			orderBy: ["createdAt", ORDER_BY.DESC]
		});

		const createOne = async (dto: CreateCollectionDto): Promise<CollectionAdapted> => {
			const collection = await useNuxtApp().$apiFetch<Collection>("/collections", {
				method: "POST",
				body: dto
			});

			return collectionNormalizeWithLocale(collection);
		};
		const getAll = async (
			dto: GetAllCollectionsDto = {},
			paginationDto?: Partial<PaginationDto>
		): Promise<PaginationResponseDto<CollectionAdapted>> => {
			const [column, orderBy] = tableSort.value.orderBy;

			recentGetAllQuery.value = {
				...getPaginationDefault(),
				...paginationDto,
				...dto,
				column,
				orderBy
			};

			const res = await useNuxtApp().$apiFetch<PaginationResponseDto<Collection>>(
				"/collections",
				{ query: recentGetAllQuery.value }
			);
			collections.value = res.data.map(collection => collectionNormalizeWithLocale(collection));
			meta.value = res.meta;

			return res as PaginationResponseDto<CollectionAdapted>;
		};
		const getById = async (id: number): Promise<CollectionAdapted> => {
			const collection = await useNuxtApp().$apiFetch<Collection>(`/collections/${id}`);

			return collectionNormalizeWithLocale(collection);
		};
		const patchById = async (
			id: number,
			dto: UpdateCollectionByIdDto
		): Promise<CollectionAdapted> => {
			const collection = await useNuxtApp().$apiFetch<Collection>(`/collections/${id}`, {
				method: "PATCH",
				body: dto
			});

			return collectionNormalizeWithLocale(collection);
		};
		const deleteById = async (id: number): Promise<IdDto> => {
			const res = await useNuxtApp().$apiFetch<IdDto>(`/collections/${id}`, {
				method: "DELETE"
			});

			return res;
		};

		return {
			currentCollection,
			collections,
			meta,
			recentGetAllQuery,
			tableSort,

			createOne,
			getAll,
			getById,
			patchById,
			deleteById
		};
	},
	{
		persist: {
			paths: ["tableSort.orderBy"]
		}
	}
);
